import { motion } from 'framer-motion'
import { Rocket, Shield, Users } from 'lucide-react'

const competencies = [
    {
        number: '01',
        icon: Rocket,
        title: 'Proprietary Acceleration',
        description:
            'The Apex Speed Core (based on CUDA/TensorRT expertise) is a market leader in optimizing inference speed for LLM/Agent workloads, delivering unparalleled performance.',
        highlights: ['CUDA/TensorRT optimization', 'Industry-leading latency', 'Custom kernel development'],
    },
    {
        number: '02',
        icon: Shield,
        title: 'Full Lifecycle Governance',
        description:
            'Provides a complete LLMOps loop, from LoRA fine-tuning to production monitoring, addressing core challenges like Agent hallucination and model drift.',
        highlights: ['End-to-end LLMOps', 'Continuous monitoring', 'Automated fine-tuning'],
    },
    {
        number: '03',
        icon: Users,
        title: 'Elite Team Background',
        description:
            'Our team possesses deep production experience across the full GPT/Agent/RAG/MLOps stack and GPU parallel computing, bringing years of expertise to every solution.',
        highlights: ['Fortune 500 experience', 'Research publications', 'Open source contributions'],
    },
]

export const Competencies = () => {
    return (
        <section className="py-24 bg-slate-900/50 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-10" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">
                        Core Competencies
                    </h2>
                    <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                        The fusion of deep engineering capability and advanced Agent architecture
                    </p>
                </motion.div>

                {/* Competencies List */}
                <div className="space-y-8">
                    {competencies.map((competency, index) => {
                        const Icon = competency.icon
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="group"
                            >
                                <div className="relative p-8 md:p-10 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-800/30 border border-slate-700/50 hover:border-primary-500/50 transition-all duration-300 backdrop-blur-sm">
                                    {/* Number */}
                                    <div className="absolute -left-4 top-8 md:-left-6 md:top-10 text-8xl md:text-9xl font-bold gradient-text opacity-20 select-none">
                                        {competency.number}
                                    </div>

                                    <div className="relative flex flex-col md:flex-row md:items-start md:space-x-8">
                                        {/* Icon */}
                                        <div className="flex-shrink-0 mb-6 md:mb-0">
                                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                                                <Icon className="w-8 h-8 md:w-10 md:h-10 text-white" />
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1">
                                            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                                                {competency.title}
                                            </h3>
                                            <p className="text-slate-400 mb-6 leading-relaxed text-lg">
                                                {competency.description}
                                            </p>

                                            {/* Highlights */}
                                            <div className="flex flex-wrap gap-3">
                                                {competency.highlights.map((highlight, hIndex) => (
                                                    <span
                                                        key={hIndex}
                                                        className="px-4 py-2 rounded-lg bg-primary-500/10 border border-primary-500/20 text-primary-300 text-sm font-medium"
                                                    >
                                                        {highlight}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Hover Gradient Effect */}
                                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-500/0 to-secondary-500/0 group-hover:from-primary-500/5 group-hover:to-secondary-500/5 transition-all duration-300 pointer-events-none" />
                                </div>
                            </motion.div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

